import { useEffect, useRef } from "react"
import { useQueryClient } from "react-query"

// Refetch active queries when the user returns to the tab after it was hidden
// for a while. Unlike refetchOnWindowFocus (which fires on every alt-tab/window
// focus), this only fires once when the tab becomes visible again after being
// backgrounded for at least MIN_HIDDEN_MS. It fixes the "left the tab open for
// a long time, came back and everything shows Network Error" case without
// spamming requests on every focus.
const MIN_HIDDEN_MS = 60 * 1000

export const useRefetchOnResume = () => {
  const queryClient = useQueryClient()
  const hiddenAtRef = useRef(null)

  useEffect(() => {
    const refetchActive = () => {
      // Only refetch queries currently mounted on screen, and let in-flight
      // requests finish rather than cancelling them.
      queryClient.refetchQueries({ active: true }, { cancelRefetch: false })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now()
      } else if (document.visibilityState === "visible") {
        const hiddenFor = hiddenAtRef.current
          ? Date.now() - hiddenAtRef.current
          : 0
        hiddenAtRef.current = null
        if (hiddenFor >= MIN_HIDDEN_MS) {
          refetchActive()
        }
      }
    }

    // Back/forward-cache restore: JS was frozen, so data is likely stale.
    const onPageShow = event => {
      if (event.persisted) {
        refetchActive()
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("pageshow", onPageShow)
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("pageshow", onPageShow)
    }
  }, [queryClient])
}
